var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { defineComponent as _defineComponent } from 'vue';
import { BAlert, BLink } from "bootstrap-vue";
import { computed, onMounted, ref, watch } from "vue";
import { RouterLink } from "vue-router";
import { useRouter } from "vue-router/composables";
import { canMutateHistory } from "@/api";
import { copyWorkflow } from "@/components/Workflow/workflows.services";
import { useHistoryItemsStore } from "@/stores/historyItemsStore";
import { useHistoryStore } from "@/stores/historyStore";
import { useUserStore } from "@/stores/userStore";
import { errorMessageAsString } from "@/utils/simple-error";
import { WorkflowRunModel } from "./model";
import { getRunData } from "./services";
import LoadingSpan from "@/components/LoadingSpan.vue";
import WorkflowRunForm from "@/components/Workflow/Run/WorkflowRunForm.vue";
import WorkflowRunFormSimple from "@/components/Workflow/Run/WorkflowRunFormSimple.vue";
import WorkflowRunSuccess from "@/components/Workflow/Run/WorkflowRunSuccess.vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'WorkflowRun',
    props: {
        workflowId: null,
        version: { default: undefined },
        preferSimpleForm: { type: Boolean, default: false },
        simpleFormTargetHistory: { default: "current" },
        simpleFormUseJobCache: { type: Boolean, default: false },
        requestState: { default: undefined },
        instance: { type: Boolean, default: false }
    },
    setup(__props, { expose }) {
        const props = __props;
        const historyStore = useHistoryStore();
        const historyItemsStore = useHistoryItemsStore();
        const userStore = useUserStore();
        const router = useRouter();
        const loading = ref(true);
        const hasUpgradeMessages = ref(false);
        const hasStepVersionChanges = ref(false);
        const invocations = ref([]);
        const simpleForm = ref(false);
        const submissionError = ref("");
        const workflowError = ref("");
        const workflowName = ref("");
        const workflowModel = ref(null);
        const currentHistoryId = computed(() => historyStore.currentHistoryId);
        const editorLink = computed(() => `/workflows/edit?id=${props.workflowId}${props.version ? `&version=${props.version}` : ""}`);
        const historyStatusKey = computed(() => `${currentHistoryId.value}_${lastUpdateTime.value}`);
        const isOwner = computed(() => userStore.matchesCurrentUsername(workflowModel.value.runData.owner));
        const lastUpdateTime = computed(() => historyItemsStore.lastUpdateTime);
        const canRunOnHistory = computed(() => {
            var _a;
            if (!currentHistoryId.value) {
                return false;
            }
            const history = historyStore.getHistoryById(currentHistoryId.value);
            return (_a = (history && canMutateHistory(history))) !== null && _a !== void 0 ? _a : false;
        });
        function handleInvocations(incomingInvocations) {
            invocations.value = incomingInvocations;
            // make sure any new histories are added to historyStore
            invocations.value.forEach((invocation) => {
                historyStore.getHistoryById(invocation.history_id);
            });
        }
        function handleSubmissionError(error) {
            submissionError.value = errorMessageAsString(error);
        }
        function loadRun() {
            return __awaiter(this, void 0, void 0, function* () {
                try {
                    const runData = yield getRunData(props.workflowId, props.version || undefined, props.instance);
                    const incomingModel = new WorkflowRunModel(runData);
                    simpleForm.value = props.preferSimpleForm;
                    if (simpleForm.value) {
                        // These only work with PJA - the API doesn't evaluate them at
                        // all outside that context currently. The main workflow form renders
                        // these dynamically and takes care of all the validation and setup details
                        // on the frontend. If these are implemented on the backend at some
                        // point this restriction can be lifted.
                        if (incomingModel.hasReplacementParametersInToolForm) {
                            console.log("cannot render simple workflow form - has ${} values in tool steps");
                            simpleForm.value = false;
                        }
                        // If there are required parameters in a tool form (a disconnected runtime
                        // input), we have to render the tool form steps and cannot use the
                        // simplified tool form.
                        if (incomingModel.hasOpenToolSteps) {
                            console.log("cannot render simple workflow form - one or more tools have disconnected runtime inputs");
                            simpleForm.value = false;
                        }
                        // Just render the whole form for resource request parameters (kind of
                        // niche - I'm not sure anyone is using these currently anyway).
                        if (incomingModel.hasWorkflowResourceParameters) {
                            console.log(`Cannot render simple workflow form - workflow resource parameters are configured`);
                            simpleForm.value = false;
                        }
                    }
                    hasUpgradeMessages.value = incomingModel.hasUpgradeMessages;
                    hasStepVersionChanges.value = incomingModel.hasStepVersionChanges;
                    workflowName.value = incomingModel.name;
                    workflowModel.value = incomingModel;
                    loading.value = false;
                }
                catch (e) {
                    workflowError.value = errorMessageAsString(e);
                }
            });
        }
        function onImport() {
            return __awaiter(this, void 0, void 0, function* () {
                const response = yield copyWorkflow(props.workflowId, workflowModel.value.runData.owner, props.version);
                router.push(`/workflows/edit?id=${response.id}`);
            });
        }
        const advancedForm = ref(false);
        const fromVariant = computed(() => {
            if (advancedForm.value) {
                return "advanced";
            }
            else if (simpleForm.value) {
                return "simple";
            }
            else {
                return "advanced";
            }
        });
        function showAdvanced() {
            advancedForm.value = true;
        }
        onMounted(() => {
            loadRun();
        });
        watch(() => historyStatusKey.value, () => {
            if (invocations.value.length === 0) {
                loadRun();
            }
        });
        expose({
            loading,
            simpleForm,
            submissionError,
            handleSubmissionError,
            workflowError,
            workflowModel,
        });
        return { __sfc: true, historyStore, historyItemsStore, userStore, router, props, loading, hasUpgradeMessages, hasStepVersionChanges, invocations, simpleForm, submissionError, workflowError, workflowName, workflowModel, currentHistoryId, editorLink, historyStatusKey, isOwner, lastUpdateTime, canRunOnHistory, handleInvocations, handleSubmissionError, loadRun, onImport, advancedForm, fromVariant, showAdvanced, BAlert, BLink, RouterLink, LoadingSpan, WorkflowRunForm, WorkflowRunFormSimple, WorkflowRunSuccess };
    }
});
